import { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, onSnapshot, query, orderBy,
  doc, updateDoc, deleteDoc, arrayUnion, arrayRemove,
} from "firebase/firestore";
import { inputStyle, btnStyle, btnSecondaryStyle, cardStyle, labelStyle, colors } from "./lib/theme";
import { DAY_NAMES, formatDayTime, todayKey } from "./lib/weekUtils";

export default function AdminPage({ user }) {
  var [venues, setVenues] = useState([]);
  var [templates, setTemplates] = useState([]);
  var [messages, setMessages] = useState([]);
  var [players, setPlayers] = useState([]);

  var [venueName, setVenueName] = useState("");
  var [venueAddress, setVenueAddress] = useState("");
  var [venueCourts, setVenueCourts] = useState(2);
  var [venueIndoor, setVenueIndoor] = useState(false);
  var [editingVenueId, setEditingVenueId] = useState(null);
  var [venueEdit, setVenueEdit] = useState({});

  var [tplVenueId, setTplVenueId] = useState("");
  var [tplDay, setTplDay] = useState("2"); // Tuesday
  var [tplTime, setTplTime] = useState("19:00");
  var [tplEndTime, setTplEndTime] = useState("20:00");
  var [tplNote, setTplNote] = useState("For all standards — beginners welcome");
  var [editingTplId, setEditingTplId] = useState(null);
  var [tplEdit, setTplEdit] = useState({});
  var [excludeDateInput, setExcludeDateInput] = useState({}); // templateId -> "YYYY-MM-DD"

  useEffect(function () {
    var unsubVenues = onSnapshot(query(collection(db, "venues"), orderBy("name")), function (snap) {
      setVenues(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubTpl = onSnapshot(collection(db, "sessionTemplates"), function (snap) {
      setTemplates(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubMsgs = onSnapshot(collection(db, "contactMessages"), function (snap) {
      setMessages(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubPlayers = onSnapshot(collection(db, "users"), function (snap) {
      setPlayers(snap.docs.map(function (d) { return { uid: d.id, ...d.data() }; }));
    });
    return function () { unsubVenues(); unsubTpl(); unsubMsgs(); unsubPlayers(); };
  }, []);

  function toggleHandled(m) {
    updateDoc(doc(db, "contactMessages", m.id), { handled: !m.handled });
  }

  function togglePlayerActive(p) {
    var willDeactivate = p.active !== false;
    var msg = willDeactivate
      ? "Deactivate " + p.displayName + "? They'll be signed out and won't be able to log back in, but their past results and stats stay intact. You can reactivate them any time."
      : "Reactivate " + p.displayName + "? They'll be able to log in again.";
    if (window.confirm(msg)) {
      updateDoc(doc(db, "users", p.uid), { active: !willDeactivate });
    }
  }

  function addVenue(e) {
    e.preventDefault();
    if (!venueName.trim()) return;
    addDoc(collection(db, "venues"), {
      name: venueName.trim(),
      address: venueAddress.trim(),
      courtCount: parseInt(venueCourts, 10) || 1,
      indoor: venueIndoor,
      createdAt: new Date().toISOString(),
    });
    setVenueName(""); setVenueAddress(""); setVenueCourts(2); setVenueIndoor(false);
  }

  function startEditVenue(v) {
    setEditingVenueId(v.id);
    setVenueEdit({ name: v.name, address: v.address || "", courtCount: v.courtCount || 1, indoor: !!v.indoor });
  }

  function saveVenue(v) {
    updateDoc(doc(db, "venues", v.id), {
      name: venueEdit.name.trim(),
      address: venueEdit.address.trim(),
      courtCount: parseInt(venueEdit.courtCount, 10) || 1,
      indoor: venueEdit.indoor,
    });
    setEditingVenueId(null);
  }

  function addTemplate(e) {
    e.preventDefault();
    if (!tplVenueId) return;
    var venue = venues.find(function (v) { return v.id === tplVenueId; });
    addDoc(collection(db, "sessionTemplates"), {
      venueId: tplVenueId,
      venueName: venue ? venue.name : "",
      dayOfWeek: parseInt(tplDay, 10),
      time: tplTime,
      endTime: tplEndTime,
      note: tplNote.trim(),
      active: true,
      excludedDates: [],
      createdAt: new Date().toISOString(),
    });
    setTplNote("For all standards — beginners welcome");
  }

  function startEditTemplate(t) {
    setEditingTplId(t.id);
    setTplEdit({ venueId: t.venueId, dayOfWeek: String(t.dayOfWeek), time: t.time, endTime: t.endTime || "", note: t.note || "" });
  }

  function saveTemplate(t) {
    var venue = venues.find(function (v) { return v.id === tplEdit.venueId; });
    updateDoc(doc(db, "sessionTemplates", t.id), {
      venueId: tplEdit.venueId,
      venueName: venue ? venue.name : t.venueName,
      dayOfWeek: parseInt(tplEdit.dayOfWeek, 10),
      time: tplEdit.time,
      endTime: tplEdit.endTime,
      note: tplEdit.note.trim(),
    });
    setEditingTplId(null);
  }

  function toggleActive(tpl) {
    updateDoc(doc(db, "sessionTemplates", tpl.id), { active: !tpl.active });
  }

  function removeTemplate(tpl) {
    if (window.confirm("Delete this recurring session? This won't remove past results.")) {
      deleteDoc(doc(db, "sessionTemplates", tpl.id));
    }
  }

  function addExclusion(tpl) {
    var d = excludeDateInput[tpl.id];
    if (!d) return;
    updateDoc(doc(db, "sessionTemplates", tpl.id), { excludedDates: arrayUnion(d) });
    setExcludeDateInput(function (prev) { return { ...prev, [tpl.id]: "" }; });
  }

  function removeExclusion(tpl, d) {
    updateDoc(doc(db, "sessionTemplates", tpl.id), { excludedDates: arrayRemove(d) });
  }

  var sortedMessages = messages.slice().sort(function (a, b) {
    var order = (a.handled ? 1 : 0) - (b.handled ? 1 : 0);
    if (order !== 0) return order;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
  var unhandledCount = messages.filter(function (m) { return !m.handled; }).length;
  var today = todayKey();
  var sortedPlayers = players.slice().sort(function (a, b) {
    var order = ((a.active === false) ? 1 : 0) - ((b.active === false) ? 1 : 0);
    if (order !== 0) return order;
    return (a.displayName || "").localeCompare(b.displayName || "");
  });

  return (
    <div>
      <h2 style={{ fontSize: 18, marginBottom: 16 }}>Admin</h2>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>
          {"Contact Messages"}{unhandledCount > 0 ? " (" + unhandledCount + " new)" : ""}
        </h3>
        {sortedMessages.length === 0 && <p style={{ fontSize: 13, color: colors.textDim }}>No messages yet.</p>}
        {sortedMessages.map(function (m) {
          return (
            <div key={m.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border, opacity: m.handled ? 0.55 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: colors.textDim }}>{m.email + " · " + m.phone}</div>
                </div>
                <button onClick={function () { toggleHandled(m); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 11 }}>
                  {m.handled ? "Mark unread" : "Mark handled"}
                </button>
              </div>
              {m.message && <p style={{ fontSize: 13, color: colors.text, margin: "8px 0 0" }}>{m.message}</p>}
              <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 6 }}>{new Date(m.createdAt).toLocaleString("en-GB")}</div>
            </div>
          );
        })}
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Players</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Deactivating blocks login and hides them from session rosters, but keeps their name on past results
          intact — it's reversible. There's no permanent delete here on purpose (it would break everyone else's
          match history); if you really need to erase someone entirely, that has to be done directly in the
          Firebase console (Authentication + Firestore).
        </p>
        {sortedPlayers.map(function (p) {
          var isSelf = user && p.uid === user.uid;
          var deactivated = p.active === false;
          return (
            <div key={p.uid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + colors.border, fontSize: 14, opacity: deactivated ? 0.55 : 1 }}>
              <div>
                <strong>{p.displayName}</strong>
                {p.isAdmin && <span style={{ fontSize: 11, color: colors.accent2, marginLeft: 6 }}>admin</span>}
                {deactivated && <span style={{ fontSize: 11, color: colors.danger, marginLeft: 6 }}>deactivated</span>}
                <div style={{ fontSize: 12, color: colors.textDim }}>{p.email}</div>
              </div>
              {!isSelf && (
                <button
                  onClick={function () { togglePlayerActive(p); }}
                  style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12, color: deactivated ? colors.accent2 : colors.danger, borderColor: deactivated ? colors.borderStrong : colors.dangerBorder }}
                >
                  {deactivated ? "Reactivate" : "Deactivate"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Venues</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Shown on the public Sessions page, and court count sets the "session full" warning on the Poll.
        </p>
        {venues.map(function (v) {
          if (editingVenueId === v.id) {
            return (
              <div key={v.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border, display: "flex", flexDirection: "column", gap: 8 }}>
                <input style={inputStyle} value={venueEdit.name} onChange={function (e) { setVenueEdit(function (p) { return { ...p, name: e.target.value }; }); }} placeholder="Venue name" />
                <input style={inputStyle} value={venueEdit.address} onChange={function (e) { setVenueEdit(function (p) { return { ...p, address: e.target.value }; }); }} placeholder="Address / postcode" />
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="1" value={venueEdit.courtCount} onChange={function (e) { setVenueEdit(function (p) { return { ...p, courtCount: e.target.value }; }); }} />
                  <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={venueEdit.indoor} onChange={function (e) { setVenueEdit(function (p) { return { ...p, indoor: e.target.checked }; }); }} />
                    Indoor
                  </label>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={function () { saveVenue(v); }} style={{ ...btnStyle, width: "auto", padding: "8px 16px" }}>Save</button>
                  <button onClick={function () { setEditingVenueId(null); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "8px 16px" }}>Cancel</button>
                </div>
              </div>
            );
          }
          return (
            <div key={v.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + colors.border, fontSize: 14 }}>
              <div>
                <strong>{v.name}</strong>{v.address ? " — " + v.address : ""}
                <div style={{ fontSize: 12, color: colors.textDim }}>{(v.courtCount || 1) + " court(s)" + (v.indoor ? " · indoor" : "")}</div>
              </div>
              <button onClick={function () { startEditVenue(v); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12 }}>Edit</button>
            </div>
          );
        })}
        <form onSubmit={addVenue} style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={labelStyle}>Venue Name</div>
            <input style={inputStyle} value={venueName} onChange={function (e) { setVenueName(e.target.value); }} placeholder="Wattisfield Village Hall" />
          </div>
          <div>
            <div style={labelStyle}>Address (optional)</div>
            <input style={inputStyle} value={venueAddress} onChange={function (e) { setVenueAddress(e.target.value); }} placeholder="..." />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Courts</div>
              <input style={inputStyle} type="number" min="1" value={venueCourts} onChange={function (e) { setVenueCourts(e.target.value); }} />
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "flex-end", paddingBottom: 11 }}>
              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={venueIndoor} onChange={function (e) { setVenueIndoor(e.target.checked); }} />
                Indoor
              </label>
            </div>
          </div>
          <button type="submit" style={btnStyle}>Add Venue</button>
        </form>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Weekly Sessions</h3>
        {templates.map(function (t) {
          var upcomingExclusions = (t.excludedDates || []).filter(function (d) { return d >= today; }).sort();
          if (editingTplId === t.id) {
            return (
              <div key={t.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border, display: "flex", flexDirection: "column", gap: 8 }}>
                <select style={inputStyle} value={tplEdit.venueId} onChange={function (e) { setTplEdit(function (p) { return { ...p, venueId: e.target.value }; }); }}>
                  {venues.map(function (v) { return <option key={v.id} value={v.id}>{v.name}</option>; })}
                </select>
                <select style={inputStyle} value={tplEdit.dayOfWeek} onChange={function (e) { setTplEdit(function (p) { return { ...p, dayOfWeek: e.target.value }; }); }}>
                  {DAY_NAMES.map(function (name, i) { return <option key={i} value={i}>{name}</option>; })}
                </select>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input style={inputStyle} type="time" value={tplEdit.time} onChange={function (e) { setTplEdit(function (p) { return { ...p, time: e.target.value }; }); }} />
                  <span style={{ fontSize: 13, color: colors.textDim }}>to</span>
                  <input style={inputStyle} type="time" value={tplEdit.endTime} onChange={function (e) { setTplEdit(function (p) { return { ...p, endTime: e.target.value }; }); }} />
                </div>
                <input style={inputStyle} value={tplEdit.note} onChange={function (e) { setTplEdit(function (p) { return { ...p, note: e.target.value }; }); }} placeholder="Note shown on public Sessions page" />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={function () { saveTemplate(t); }} style={{ ...btnStyle, width: "auto", padding: "8px 16px" }}>Save</button>
                  <button onClick={function () { setEditingTplId(null); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "8px 16px" }}>Cancel</button>
                </div>
              </div>
            );
          }
          return (
            <div key={t.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{formatDayTime(t.dayOfWeek, t.time, t.endTime)}</div>
                  <div style={{ fontSize: 12, color: colors.textDim }}>{t.venueName}{!t.active ? " · inactive" : ""}</div>
                  {t.note && <div style={{ fontSize: 12, color: colors.textFaint, marginTop: 2 }}>{t.note}</div>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={function () { startEditTemplate(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12 }}>Edit</button>
                  <button onClick={function () { toggleActive(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12 }}>
                    {t.active ? "Pause" : "Resume"}
                  </button>
                  <button onClick={function () { removeTemplate(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12, color: colors.danger, borderColor: colors.dangerBorder }}>
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 10, paddingLeft: 2 }}>
                <div style={{ fontSize: 11, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Skip a Date</div>
                {upcomingExclusions.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {upcomingExclusions.map(function (d) {
                      return (
                        <span key={d} style={{ fontSize: 12, background: colors.tealTint, color: colors.tealDark, padding: "3px 8px", borderRadius: 12, display: "flex", alignItems: "center", gap: 6 }}>
                          {d}
                          <button onClick={function () { removeExclusion(t, d); }} style={{ background: "none", border: "none", cursor: "pointer", color: colors.tealDark, fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    style={{ ...inputStyle, width: "auto", flex: 1 }}
                    type="date"
                    value={excludeDateInput[t.id] || ""}
                    onChange={function (e) { setExcludeDateInput(function (prev) { return { ...prev, [t.id]: e.target.value }; }); }}
                  />
                  <button onClick={function () { addExclusion(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "0 16px", fontSize: 12 }}>Exclude</button>
                </div>
              </div>
            </div>
          );
        })}
        {venues.length === 0 ? (
          <p style={{ fontSize: 13, color: colors.textDim }}>Add a venue first.</p>
        ) : (
          <form onSubmit={addTemplate} style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={labelStyle}>Venue</div>
              <select style={inputStyle} value={tplVenueId} onChange={function (e) { setTplVenueId(e.target.value); }}>
                <option value="">Select a venue…</option>
                {venues.map(function (v) { return <option key={v.id} value={v.id}>{v.name}</option>; })}
              </select>
            </div>
            <div>
              <div style={labelStyle}>Day</div>
              <select style={inputStyle} value={tplDay} onChange={function (e) { setTplDay(e.target.value); }}>
                {DAY_NAMES.map(function (name, i) { return <option key={i} value={i}>{name}</option>; })}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>From</div>
                <input style={inputStyle} type="time" value={tplTime} onChange={function (e) { setTplTime(e.target.value); }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>To</div>
                <input style={inputStyle} type="time" value={tplEndTime} onChange={function (e) { setTplEndTime(e.target.value); }} />
              </div>
            </div>
            <div>
              <div style={labelStyle}>Note (shown on public Sessions page)</div>
              <input style={inputStyle} value={tplNote} onChange={function (e) { setTplNote(e.target.value); }} placeholder="For all standards — beginners welcome" />
            </div>
            <button type="submit" style={btnStyle}>Add Weekly Session</button>
          </form>
        )}
      </div>
    </div>
  );
}
